import { InternalId } from '@edgebox/data-definition-kit';
import { Modal } from '@edgebox/react-components';
import { MonitoringPromptStatus, MonitoringPromptType, SyncCoreDataDefinitionsEnumTranslator } from '@edgebox/sync-core-data-definitions';
import { ClientMonitoringPromptEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { EmbeddedModal } from '../../../EmbeddedModal';
import { IMonitoringPromptEligibility, isEligiblePrompt, NON_PREVIOUS_PROMPT_STATUSES } from './termPromptAssociations.utils';
import { BP_PORTAL_CLASS_NAME, BP_PRIMARY_BUTTON_CLASSES } from './brandPresenceClasses';

const PROMPTS_PER_PAGE = 25;

/**
 * Page size used while holding the whole eligible set. Bigger than the page the operator sees, so
 * the number of round trips stays low; the endpoint caps a page well above this.
 */
const FULL_LOAD_PAGE_SIZE = 100;

export interface IMonitoringPromptPickerModalProps {
  api: ISyncCoreApi;
  show: boolean;
  title: string;
  /** Rendered above the list, e.g. what the selection will be used for. */
  intro?: React.ReactNode;
  /** Prompts already associated. They stay visible but cannot be selected again. */
  alreadySelectedPromptIds?: string[];
  /**
   * Which prompts this picker may offer, and what to say when none qualify. Omitted, every root
   * prompt of the project is offered.
   */
  eligibility?: IMonitoringPromptEligibility;
  saving?: boolean;
  error?: string;
  onHide: () => void;
  onConfirm: (promptIds: InternalId[]) => void;
}

/**
 * Pick monitoring prompts to associate a term with.
 *
 * The picker cannot offer whatever the list endpoint happens to return: what a term can be
 * associated with depends on the association it is being added to, and offering the rest produces
 * either a failed request or a silently dead assignment. `eligibility` carries that rule.
 *
 * Two of the three narrowings are server-side. `type` is a real list filter. Template-generated
 * instances are excluded by the endpoint itself — it drops them unless a `templatePromptKey` filter
 * asks for them — and `isEligiblePrompt` re-checks the rows anyway, so the picker's own contract
 * does not rest on that default.
 *
 * The template facet is NOT a list filter, so it can only be applied here. Applying it to one server
 * page would silently hide eligible templates sitting on later pages, so a picker that carries a
 * facet loads every page of its type-filtered set up front and pages it in the browser instead. The
 * cost is one request per `FULL_LOAD_PAGE_SIZE` prompts of that type when the picker opens; the
 * server-side type filter is what keeps the count small.
 */
export function MonitoringPromptPickerModal({
  api,
  show,
  title,
  intro,
  alreadySelectedPromptIds,
  eligibility,
  saving,
  error,
  onHide,
  onConfirm,
}: IMonitoringPromptPickerModalProps) {
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<MonitoringPromptStatus | 'all'>('all');
  const [engineFilterKey, setEngineFilterKey] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [numberOfPages, setNumberOfPages] = React.useState(1);
  const [prompts, setPrompts] = React.useState<ClientMonitoringPromptEntity[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | undefined>(undefined);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const alreadySelected = React.useMemo(() => new Set(alreadySelectedPromptIds ?? []), [alreadySelectedPromptIds]);

  // Reduced to primitives so a caller that rebuilds its eligibility object every render cannot turn
  // the load effect below into a fetch loop.
  const templateFacet = eligibility?.templateFacet;
  const promptTypesKey = (eligibility?.types ?? []).join(',');

  /** Whether the whole eligible set is held here, because a filter the endpoint lacks narrows it. */
  const holdsFullSet = !!templateFacet;
  const serverPage = holdsFullSet ? 0 : page;

  React.useEffect(() => {
    if (!show) {
      return;
    }

    setSelectedIds([]);
    setSearch('');
    setStatusFilter('all');
    setEngineFilterKey('');
    setPage(0);
  }, [show]);

  React.useEffect(() => {
    setPage(0);
  }, [statusFilter, engineFilterKey]);

  // While the whole set is held here the name filter narrows all of it, so the page the operator was
  // on may no longer exist. Paging server-side it stays a within-page filter and the page stands.
  React.useEffect(() => {
    if (holdsFullSet) {
      setPage(0);
    }
  }, [holdsFullSet, search]);

  React.useEffect(() => {
    if (!show) {
      return;
    }

    let active = true;

    void (async () => {
      setLoading(true);
      setLoadError(undefined);

      const filters = {
        status: statusFilter === 'all' ? NON_PREVIOUS_PROMPT_STATUSES : [statusFilter],
        enginesKey: engineFilterKey || undefined,
        type: promptTypesKey ? (promptTypesKey.split(',') as MonitoringPromptType[]) : undefined,
      };

      try {
        if (holdsFullSet) {
          const collected: ClientMonitoringPromptEntity[] = [];
          let nextPage = 0;
          let pageCount = 1;

          do {
            const result = await api.governance.monitoringPrompts.list({ page: nextPage, itemsPerPage: FULL_LOAD_PAGE_SIZE }, filters);
            collected.push(...(result.items || []));
            pageCount = Math.max(1, result.numberOfPages || 1);
            nextPage += 1;
          } while (nextPage < pageCount);

          if (!active) {
            return;
          }

          setPrompts(collected);
          setNumberOfPages(1);
          return;
        }

        const result = await api.governance.monitoringPrompts.list({ page: serverPage, itemsPerPage: PROMPTS_PER_PAGE }, filters);

        if (!active) {
          return;
        }

        setPrompts(result.items || []);
        setNumberOfPages(Math.max(1, result.numberOfPages || 1));
      } catch (requestError) {
        if (active) {
          setLoadError((requestError as Error)?.message || 'Failed to load prompts.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [api, engineFilterKey, holdsFullSet, promptTypesKey, serverPage, show, statusFilter]);

  // Applied to what the server returned rather than in place of a filter: `type` already narrowed
  // the request, and this drops the rows the endpoint cannot express — a template of the wrong
  // facet, and any generated instance that a change of endpoint default might one day let through.
  const eligiblePrompts = React.useMemo(() => prompts.filter((prompt) => isEligiblePrompt(prompt, eligibility)), [eligibility, prompts]);

  // Search narrows the whole eligible set when it is held here, and the loaded page otherwise —
  // which is why the input says which one it is doing. A name filter is not offered by the list
  // endpoint, and pretending otherwise would hide matches on later pages.
  const matchedPrompts = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return eligiblePrompts;
    }
    return eligiblePrompts.filter((prompt) => `${prompt.name} ${prompt.promptText}`.toLowerCase().includes(needle));
  }, [eligiblePrompts, search]);

  const pageCount = holdsFullSet ? Math.max(1, Math.ceil(matchedPrompts.length / PROMPTS_PER_PAGE)) : numberOfPages;
  const visiblePrompts = holdsFullSet
    ? matchedPrompts.slice(page * PROMPTS_PER_PAGE, page * PROMPTS_PER_PAGE + PROMPTS_PER_PAGE)
    : matchedPrompts;

  // An empty list is ambiguous — it reads as a failed load. Say WHY it is empty: the operator's own
  // filters when they narrowed it, and otherwise the rule that leaves this term with nothing to
  // pick, which is a fact about the project rather than about this request.
  const operatorNarrowed = !!search.trim() || statusFilter !== 'all' || !!engineFilterKey;
  const emptyMessage =
    !eligiblePrompts.length && eligibility && !operatorNarrowed ? eligibility.emptyExplanation : 'No prompts match these filters.';

  const toggle = (promptId: string) => {
    setSelectedIds((current) => (current.includes(promptId) ? current.filter((id) => id !== promptId) : [...current, promptId]));
  };

  const selectableOnPage = visiblePrompts.map((prompt) => String(prompt.id)).filter((id) => !alreadySelected.has(id));
  const allOnPageSelected = !!selectableOnPage.length && selectableOnPage.every((id) => selectedIds.includes(id));

  return (
    <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {intro ? <div className="mb-4 text-sm text-slate-600">{intro}</div> : null}

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={holdsFullSet ? 'Filter by name…' : 'Filter this page by name…'}
            className="min-w-[14rem] flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as MonitoringPromptStatus | 'all')}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="all">Any status</option>
            {NON_PREVIOUS_PROMPT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptStatus', status)}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={engineFilterKey}
            onChange={(event) => setEngineFilterKey(event.target.value)}
            placeholder="Engine term key…"
            className="w-44 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-sm transition-all duration-150 focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/10"
          />
        </div>

        {loading ? <div className="text-sm text-slate-500">Loading prompts…</div> : null}
        {loadError ? <div className="text-sm text-rose-600">{loadError}</div> : null}

        {!loading && !loadError ? (
          <div className="overflow-hidden rounded-xl ring-1 ring-inset ring-slate-200/80">
            <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <input
                type="checkbox"
                aria-label="Select every prompt on this page"
                checked={allOnPageSelected}
                disabled={!selectableOnPage.length}
                onChange={() =>
                  setSelectedIds((current) =>
                    allOnPageSelected
                      ? current.filter((id) => !selectableOnPage.includes(id))
                      : [...current, ...selectableOnPage.filter((id) => !current.includes(id))]
                  )
                }
              />
              <span className="flex-1">Prompt</span>
              <span className="w-24">Status</span>
            </div>

            {!visiblePrompts.length ? (
              <div className="px-4 py-4 text-sm text-slate-500">{emptyMessage}</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {visiblePrompts.map((prompt) => {
                  const promptId = String(prompt.id);
                  const disabled = alreadySelected.has(promptId);

                  return (
                    <label
                      key={promptId}
                      className={`flex items-start gap-3 px-4 py-3 text-sm ${disabled ? 'bg-slate-50 text-slate-400' : 'cursor-pointer hover:bg-slate-50'}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={disabled || selectedIds.includes(promptId)}
                        disabled={disabled}
                        onChange={() => toggle(promptId)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold text-slate-900">{prompt.name}</span>
                        <span className="mt-0.5 block truncate text-xs text-slate-500">{prompt.promptText}</span>
                        {disabled ? <span className="mt-1 block text-xs italic">Already associated.</span> : null}
                      </span>
                      <span className="w-24 shrink-0 text-xs text-slate-500">
                        {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptStatus', prompt.status)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}

        {pageCount > 1 ? (
          <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50"
              disabled={page <= 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </button>
            <span>
              Page {page + 1} of {pageCount}
            </span>
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        ) : null}

        {error ? <div className="mt-3 text-sm text-rose-600">{error}</div> : null}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-between gap-3">
          {/* Selection survives paging, so the count is the only place the operator can see what a
              save will actually touch. */}
          <span className="text-sm text-slate-500">
            {selectedIds.length} {selectedIds.length === 1 ? 'prompt' : 'prompts'} selected
          </span>
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={onHide}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedIds.length || saving}
              onClick={() => onConfirm(selectedIds as InternalId[])}
              className={BP_PRIMARY_BUTTON_CLASSES}
            >
              {saving ? 'Saving…' : 'Add prompts'}
            </button>
          </span>
        </div>
      </Modal.Footer>
    </EmbeddedModal>
  );
}
